import { STORAGE_KEY, STATE_KEYS } from './StateConstants.js';
import { createDefaultState } from './DefaultState.js';
import { DeckState } from './DeckState.js';
import { MapState } from './MapState.js';
import { BattleState } from './BattleState.js';
import { ProgressionState } from './ProgressionState.js';

/**
 * GameStateManager - Coordinator for game state.
 * Delegates domain-specific operations to sub-managers while maintaining
 * backward-compatible static API.
 */
const DIALOGUE_NOTICE_KEY = 'air_notice_dialogue_boilerplate_v1';
const INTRO_SHOWN_KEY = 'air_intro_shown';
const HDR_ENABLED_KEY = 'air_hdr_enabled';
const MUSIC_ENABLED_KEY = 'air_music_enabled';

export class GameStateManager {
  static _state = null;
  static _subscribers = new Map();

  // Sub-managers (lazily initialized)
  static _deck = null;
  static _map = null;
  static _battle = null;
  static _progression = null;

  static get deck() {
    if (!this._deck) this._deck = new DeckState(this);
    return this._deck;
  }

  static get map() {
    if (!this._map) this._map = new MapState(this);
    return this._map;
  }

  static get battle() {
    if (!this._battle) this._battle = new BattleState(this);
    return this._battle;
  }

  static get progression() {
    if (!this._progression) this._progression = new ProgressionState(this);
    return this._progression;
  }

  // ==========================================
  // OBSERVER PATTERN
  // ==========================================

  static subscribe(key, callback) {
    if (!this._subscribers.has(key)) {
      this._subscribers.set(key, new Set());
    }
    this._subscribers.get(key).add(callback);
    return () => this.unsubscribe(key, callback);
  }

  static unsubscribe(key, callback) {
    const subs = this._subscribers.get(key);
    if (subs) subs.delete(callback);
  }

  static _notify(key) {
    const subs = this._subscribers.get(key);
    if (subs) {
      subs.forEach(cb => cb(this.state));
    }
  }

  // ==========================================
  // CORE
  // ==========================================

  static get state() {
    if (this._state) return this._state;

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        this._state = JSON.parse(saved);
      } else {
        this._state = createDefaultState();
      }
    } catch (error) {
      console.error('Failed to load game state:', error);
      this._state = createDefaultState();
    }

    this._recoverOrphanedDeployedCards();
    this._migrateRetiredCards();

    return this._state;
  }

  // Old saves may hold cards that no longer exist: manual_labor and the old
  // gold_mine card were both replaced by construction_squad.
  static _migrateRetiredCards() {
    const deck = this._state?.deck;
    if (!deck?.ownedCards) return;

    const RETIRED = { manual_labor: 'construction_squad', gold_mine: 'construction_squad' };
    let changed = false;

    for (const card of deck.ownedCards) {
      const newId = RETIRED[card.cardId];
      if (!newId) continue;

      const existing = deck.ownedCards.find(c => c.cardId === newId);
      if (existing && existing !== card) {
        existing.count += card.count || 0;
        existing.active = Math.min(existing.count, (existing.active || 0) + (card.active || 0));
        card.count = 0;
      } else {
        card.cardId = newId;
      }
      changed = true;
    }

    if (changed) {
      deck.ownedCards = deck.ownedCards.filter(c => (c.count || 0) > 0);
      for (const slot of deck.deckSlots || []) {
        if (!slot) continue;
        for (const [oldId, newId] of Object.entries(RETIRED)) {
          if (slot[oldId] !== undefined) {
            slot[newId] = (slot[newId] || 0) + slot[oldId];
            delete slot[oldId];
          }
        }
      }
      this.save();
      console.log('Migrated retired cards (manual_labor/gold_mine) to construction_squad');
    }
  }

  static _recoverOrphanedDeployedCards() {
    if (!this._state?.battle) return;

    const deployedCards = this._state.battle.deployedCards || [];

    if (deployedCards.length > 0) {
      console.log('Recovering orphaned deployed cards:', deployedCards);
      this._state.battle.deployedCards = [];
      this._state.battle.inProgress = false;
      this.save();
    }
  }

  static load() {
    return this.state;
  }

  static save() {
    if (!this._state) return false;

    try {
      // Clone meta before mutating to avoid partial state if save fails
      const timestamp = new Date().toISOString();
      const toSave = { ...this._state, _meta: { ...this._state._meta, lastSaved: timestamp } };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      // Only update state after successful save
      this._state._meta.lastSaved = timestamp;
      return true;
    } catch (error) {
      console.error('Failed to save game state:', error);
      return false;
    }
  }

  static reset() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(DIALOGUE_NOTICE_KEY);
      localStorage.removeItem(INTRO_SHOWN_KEY);
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
    this._state = null;
  }

  static isIntroShown() {
    return localStorage.getItem(INTRO_SHOWN_KEY) === 'true';
  }

  static setIntroShown(shown = true) {
    if (shown) {
      localStorage.setItem(INTRO_SHOWN_KEY, 'true');
    } else {
      localStorage.removeItem(INTRO_SHOWN_KEY);
    }
  }

  static isHdrEnabled() {
    const value = localStorage.getItem(HDR_ENABLED_KEY);
    return value === null || value === 'true';
  }

  static setHdrEnabled(enabled = true) {
    localStorage.setItem(HDR_ENABLED_KEY, enabled ? 'true' : 'false');
  }

  // Deprecated: volume now managed by AudioManager. Kept for backward compat.
  static isMusicEnabled() {
    return true;
  }

  static setMusicEnabled() {
    // No-op — use audioManager.setMusicVolume() / audioManager.toggleMute()
  }

  // ==========================================
  // PLAYER
  // ==========================================

  static getUsername() {
    return this.state.player.username;
  }

  static setUsername(name) {
    this.state.player.username = name;
  }

  static isLoggedIn() {
    return this.state.player.isLoggedIn === true;
  }

  static login(username) {
    this.state.player.username = username;
    this.state.player.isLoggedIn = true;
    this.save();
  }

  static logout() {
    this.state.player.username = 'Guest';
    this.state.player.isLoggedIn = false;
    this.save();
  }

  static register(username) {
    this.state.player.username = username;
    this.state.player.isLoggedIn = true;
    this.save();
  }

  static getDisplayName() {
    return this.isLoggedIn() ? this.state.player.username : 'Guest';
  }

  // ==========================================
  // RESOURCES
  // ==========================================

  static getSpirit() {
    return this.state.resources.spirit;
  }

  static getMaxSpirit() {
    return this.state.resources.maxSpirit;
  }

  static modifySpirit(delta) {
    const effectiveMax = this.getEffectiveMaxSpirit();
    this.state.resources.spirit = Math.max(0,
      Math.min(effectiveMax, this.state.resources.spirit + delta)
    );

    this._notify(STATE_KEYS.SPIRIT);

    if (this.state.resources.spirit <= 0 && this.onSpiritZero) {
      this.onSpiritZero();
    }

    return this.state.resources.spirit;
  }

  static setMaxSpirit(value) {
    this.state.resources.maxSpirit = value;
    const effectiveMax = this.getEffectiveMaxSpirit();
    this.state.resources.spirit = Math.min(this.state.resources.spirit, effectiveMax);
    this._notify(STATE_KEYS.SPIRIT);
  }

  // ==========================================
  // DECK (delegated to DeckState)
  // ==========================================

  static getOwnedCards() { return this.deck.getOwnedCards(); }
  static getActiveCards() { return this.deck.getActiveCards(); }
  static getTotalActiveCardCount() { return this.deck.getTotalActiveCardCount(); }
  static getEffectiveMaxActiveCards() { return this.deck.getEffectiveMaxActiveCards(); }
  static getEquipmentModifiers() { return this.deck.getEquipmentModifiers(); }
  static getEffectiveCardsPerTurn() { return this.deck.getEffectiveCardsPerTurn(); }
  static getEffectiveMaxEquipmentSlots() { return this.deck.getEffectiveMaxEquipmentSlots(); }
  static getEffectiveMaxSpirit() { return this.deck.getEffectiveMaxSpirit(); }
  static addCard(cardId, count = 1) { return this.deck.addCard(cardId, count); }
  static setCardActiveCount(cardId, activeCount) { return this.deck.setCardActiveCount(cardId, activeCount); }
  static drawRandomActiveCards(count) { return this.deck.drawRandomActiveCards(count); }

  // ==========================================
  // DECK SLOTS (delegated to DeckState)
  // ==========================================

  static getActiveDeckSlot() { return this.deck.getActiveDeckSlot(); }
  static getDeckSlots() { return this.deck.getDeckSlots(); }
  static switchDeckSlot(slotIndex) { return this.deck.switchDeckSlot(slotIndex); }
  static saveCurrentConfigToSlot() { return this.deck.saveCurrentConfigToSlot(); }

  // ==========================================
  // EQUIPMENT (delegated to DeckState)
  // ==========================================

  static getOwnedEquipment() { return this.deck.getOwnedEquipment(); }
  static getActiveEquipment() { return this.deck.getActiveEquipment(); }
  static addEquipment(equipmentId) { return this.deck.addEquipment(equipmentId); }
  static setEquipmentActive(equipmentId, active) { return this.deck.setEquipmentActive(equipmentId, active); }
  static getToggledEquipment() { return this.deck.getToggledEquipment(); }
  static isEquipmentToggled(equipmentId) { return this.deck.isEquipmentToggled(equipmentId); }
  static toggleEquipment(equipmentId) { return this.deck.toggleEquipment(equipmentId); }
  static setEquipmentToggled(equipmentId, toggled) { return this.deck.setEquipmentToggled(equipmentId, toggled); }
  static getToggleableEquipmentModifiers() { return this.deck.getToggleableEquipmentModifiers(); }
  static getLoadingEquipment() { return this.deck.getLoadingEquipment(); }
  static isEquipmentLoading(equipmentId) { return this.deck.isEquipmentLoading(equipmentId); }

  // ==========================================
  // HEX MAP TURN (abstract turn hook for map mode)
  // ==========================================

  static hexMapTurn() {
    this.deck.decrementEquipmentLoading();
    this.resetNpcCallTurnLimits();
  }

  // One NPC call may ring per map turn; a move opens the next slot for every caller.
  static resetNpcCallTurnLimits() {
    const npcCalls = this.state?.npcCalls;
    if (!npcCalls) return;
    for (const id of Object.keys(npcCalls)) {
      npcCalls[id].rangThisTurn = false;
    }
    this.save();
  }

  // ==========================================
  // MAP (delegated to MapState)
  // ==========================================

  static getMapSeed() { return this.map.getMapSeed(); }
  static getPlayerPosition() { return this.map.getPlayerPosition(); }
  static setPlayerPosition(q, r) { return this.map.setPlayerPosition(q, r); }
  static getExploredTiles() { return this.map.getExploredTiles(); }
  static addExploredTile(q, r) { return this.map.addExploredTile(q, r); }
  static getVisitedTiles() { return this.map.getVisitedTiles(); }
  static addVisitedTile(q, r) { return this.map.addVisitedTile(q, r); }
  static isTileVisited(q, r) { return this.map.isTileVisited(q, r); }
  static getConstructions() { return this.map.getConstructions(); }
  static addConstruction(constructionId, q, r) { return this.map.addConstruction(constructionId, q, r); }
  static getConstructionAt(q, r) { return this.map.getConstructionAt(q, r); }
  static hasConstructionAt(q, r) { return this.map.hasConstructionAt(q, r); }
  static updateConstructionState(q, r, newState) { return this.map.updateConstructionState(q, r, newState); }
  static getConstructionMovements() { return this.map.getConstructionMovements(); }
  static incrementConstructionMovements() { return this.map.incrementConstructionMovements(); }
  static resetConstructionMovements() { return this.map.resetConstructionMovements(); }
  static getClearedTiles() { return this.map.getClearedTiles(); }
  static isTileCleared(q, r) { return this.map.isTileCleared(q, r); }
  static markTileCleared(q, r) { return this.map.markTileCleared(q, r); }
  static setPendingBattle(q, r) { return this.map.setPendingBattle(q, r); }
  static getPendingBattle() { return this.map.getPendingBattle(); }
  static clearPendingBattle() { return this.map.clearPendingBattle(); }
  static setRecentlyClearedTile(q, r) { return this.map.setRecentlyClearedTile(q, r); }
  static getRecentlyClearedTile() { return this.map.getRecentlyClearedTile(); }
  static clearRecentlyClearedTile() { return this.map.clearRecentlyClearedTile(); }
  static setTileEnemyType(q, r, enemyType) { return this.map.setTileEnemyType(q, r, enemyType); }
  static getTileEnemyType(q, r) { return this.map.getTileEnemyType(q, r); }
  static setTileEncounter(q, r, config) { return this.map.setTileEncounter(q, r, config); }
  static getTileEncounter(q, r) { return this.map.getTileEncounter(q, r); }
  static setTileRewardBundle(q, r, rewardBundle) { return this.map.setTileRewardBundle(q, r, rewardBundle); }
  static getTileRewardBundle(q, r) { return this.map.getTileRewardBundle(q, r); }
  static hasTileRewardBundle(q, r) { return this.map.hasTileRewardBundle(q, r); }
  static markTileRewardBundleClaimed(q, r) { return this.map.markTileRewardBundleClaimed(q, r); }
  static clearTileRewardBundle(q, r) { return this.map.clearTileRewardBundle(q, r); }
  static updateEncounterRemaining(q, r, remainingEnemies) { return this.map.updateEncounterRemaining(q, r, remainingEnemies); }
  static clearTileEncounter(q, r) { return this.map.clearTileEncounter(q, r); }
  static setStoryNodePlacement(q, r, storyNodeId) { return this.map.setStoryNodePlacement(q, r, storyNodeId); }
  static getStoryNodeAt(q, r) { return this.map.getStoryNodeAt(q, r); }
  static getStoryNodePlacements() { return this.map.getStoryNodePlacements(); }
  static completeStoryNode(storyNodeId) { return this.map.completeStoryNode(storyNodeId); }
  static isStoryNodeComplete(storyNodeId) { return this.map.isStoryNodeComplete(storyNodeId); }
  static getCompletedStoryNodes() { return this.map.getCompletedStoryNodes(); }
  static getAnalyzedHexes() { return this.map.getAnalyzedHexes(); }
  static addAnalyzedHex(q, r) { return this.map.addAnalyzedHex(q, r); }
  static isHexAnalyzed(q, r) { return this.map.isHexAnalyzed(q, r); }
  static setPendingStoryNode(q, r) { return this.map.setPendingStoryNode(q, r); }
  static getPendingStoryNode() { return this.map.getPendingStoryNode(); }
  static clearPendingStoryNode() { return this.map.clearPendingStoryNode(); }
  static setTileTrader(q, r, traderData) { return this.map.setTileTrader(q, r, traderData); }
  static getTileTrader(q, r) { return this.map.getTileTrader(q, r); }
  static isTraderTile(q, r) { return this.map.isTraderTile(q, r); }
  static getTraderInventory(q, r) { return this.map.getTraderInventory(q, r); }
  static purchaseFromTrader(q, r, itemIndex) { return this.map.purchaseFromTrader(q, r, itemIndex); }
  static setPendingTrader(q, r) { return this.map.setPendingTrader(q, r); }
  static getPendingTrader() { return this.map.getPendingTrader(); }
  static clearPendingTrader() { return this.map.clearPendingTrader(); }
  static setPendingTraderEssence(color) { return this.map.setPendingTraderEssence(color); }
  static getPendingTraderEssence() { return this.map.getPendingTraderEssence(); }
  static clearPendingTraderEssence() { return this.map.clearPendingTraderEssence(); }
  static clearAllMapData() { return this.map.clearAllMapData(); }

  // Settlement methods
  static setSettlementTrader(q, r, traderData) { return this.map.setSettlementTrader(q, r, traderData); }
  static getSettlementTrader(q, r) { return this.map.getSettlementTrader(q, r); }
  static purchaseFromSettlement(q, r, itemIndex) { return this.map.purchaseFromSettlement(q, r, itemIndex); }
  static discoverSettlement(storyNodeId) { return this.map.discoverSettlement(storyNodeId); }
  static isSettlementDiscovered(storyNodeId) { return this.map.isSettlementDiscovered(storyNodeId); }
  static getDiscoveredSettlements() { return this.map.getDiscoveredSettlements(); }
  static setLastSettlementPosition(q, r) { return this.map.setLastSettlementPosition(q, r); }
  static getLastSettlementPosition() { return this.map.getLastSettlementPosition(); }
  static clearLastSettlementPosition() { return this.map.clearLastSettlementPosition(); }

  // Home settlement methods
  static setHomePosition(q, r) { return this.map.setHomePosition(q, r); }
  static getHomePosition() { return this.map.getHomePosition(); }
  static hasHome() { return this.map.hasHome(); }
  static isHomeTile(q, r) { return this.map.isHomeTile(q, r); }

  // ==========================================
  // BLUEPRINTS (delegated to MapState)
  // ==========================================

  static getBlueprints() { return this.map.getBlueprints(); }
  static hasBlueprint(blueprintId) { return this.map.hasBlueprint(blueprintId); }
  static addBlueprint(blueprintId) { return this.map.addBlueprint(blueprintId); }

  // ==========================================
  // DISCOVERED TRADER ITEMS (delegated to MapState)
  // ==========================================

  static getDiscoveredTraderEquipment() { return this.map.getDiscoveredTraderEquipment(); }
  static addDiscoveredTraderEquipment(equipmentId) { return this.map.addDiscoveredTraderEquipment(equipmentId); }
  static getDiscoveredTraderBlueprints() { return this.map.getDiscoveredTraderBlueprints(); }
  static addDiscoveredTraderBlueprint(blueprintId) { return this.map.addDiscoveredTraderBlueprint(blueprintId); }

  // ==========================================
  // BATTLE (delegated to BattleState)
  // ==========================================

  static isBattleInProgress() { return this.battle.isBattleInProgress(); }
  static startBattle() { return this.battle.startBattle(); }
  static endBattle() { return this.battle.endBattle(); }
  static addDefeatedEnemy(enemyData) { return this.battle.addDefeatedEnemy(enemyData); }
  static getDefeatedEnemies() { return this.battle.getDefeatedEnemies(); }
  static clearDefeatedEnemies() { return this.battle.clearDefeatedEnemies(); }
  static getBattleResources() { return this.battle.getBattleResources(); }
  static getBattleResource(type) { return this.battle.getBattleResource(type); }
  static addBattleResource(type, amount) { return this.battle.addBattleResource(type, amount); }
  static removeBattleResource(type, amount) { return this.battle.removeBattleResource(type, amount); }
  static resetBattleResources() { return this.battle.resetBattleResources(); }
  static deployCard(cardId) { return this.battle.deployCard(cardId); }
  static returnCard(cardId) { return this.battle.returnCard(cardId); }
  static getDeployedCards() { return this.battle.getDeployedCards(); }
  static clearDeployedCards() { return this.battle.clearDeployedCards(); }
  static getCardsInHand() { return this.battle.getCardsInHand(); }
  static setCardsInHand(cardIds) { return this.battle.setCardsInHand(cardIds); }
  static addCardToHand(cardId) { return this.battle.addCardToHand(cardId); }
  static removeCardFromHand(cardId) { return this.battle.removeCardFromHand(cardId); }
  static clearCardsInHand() { return this.battle.clearCardsInHand(); }
  static getBattleBuffs() { return this.battle.getBattleBuffs(); }
  static addBattleBuff(buffType, value) { return this.battle.addBattleBuff(buffType, value); }
  static resetBattleBuffs() { return this.battle.resetBattleBuffs(); }

  // ==========================================
  // MEMORIES (delegated to ProgressionState)
  // ==========================================

  static getMemories() { return this.progression.getMemories(); }
  static hasMemory(memoryId) { return this.progression.hasMemory(memoryId); }
  static addMemory(memoryId) { return this.progression.addMemory(memoryId); }

  // ==========================================
  // ENCYCLOPEDIA (delegated to ProgressionState)
  // ==========================================

  static getEncyclopedia() { return this.progression.getEncyclopedia(); }
  static knowsEnemy(enemyId) { return this.progression.knowsEnemy(enemyId); }
  static learnEnemy(enemyId) { return this.progression.learnEnemy(enemyId); }

  // ==========================================
  // DIALOGUE (delegated to ProgressionState)
  // ==========================================

  static getCompletedDialogues() { return this.progression.getCompletedDialogues(); }
  static isDialogueComplete(sceneId) { return this.progression.isDialogueComplete(sceneId); }
  static markDialogueComplete(sceneId) { return this.progression.markDialogueComplete(sceneId); }
  static getChoiceHistory() { return this.progression.getChoiceHistory(); }
  static recordChoice(nodeId, choiceIndex) { return this.progression.recordChoice(nodeId, choiceIndex); }
  static wasChoiceSelected(nodeId, choiceIndex) { return this.progression.wasChoiceSelected(nodeId, choiceIndex); }

  // ==========================================
  // RESURRECTION (delegated to ProgressionState)
  // ==========================================

  static getMedicPosition() { return this.progression.getMedicPosition(); }
  static setMedicPosition(q, r) { return this.progression.setMedicPosition(q, r); }
  static getResurrectionSpirit() { return this.progression.getResurrectionSpirit(); }
  static setResurrectionSpirit(amount) { return this.progression.setResurrectionSpirit(amount); }
  static hasResurrection() { return this.progression.hasResurrection(); }
  static consumeResurrection() { return this.progression.consumeResurrection(); }

  // ==========================================
  // SCIENTIST (delegated to ProgressionState)
  // ==========================================

  static getScientistDeposited() { return this.progression.getScientistDeposited(); }
  static addScientistDeposit(amount) { return this.progression.addScientistDeposit(amount); }
  static hasWonGame() { return this.progression.hasWonGame(); }

  // ==========================================
  // TRAITS & RELATIONS (delegated to ProgressionState)
  // ==========================================

  static getTrait(name) { return this.progression.getTrait(name); }
  static getTraits() { return this.progression.getTraits(); }
  static modifyTrait(name, delta) { return this.progression.modifyTrait(name, delta); }
  static getRelation(name) { return this.progression.getRelation(name); }
  static getRelations() { return this.progression.getRelations(); }
  static modifyRelation(name, delta) { return this.progression.modifyRelation(name, delta); }

  // ==========================================
  // TRAIT CHECKS (delegated to ProgressionState)
  // ==========================================

  static recordFailedWhiteCheck(checkId, trait, traitLevel) { return this.progression.recordFailedWhiteCheck(checkId, trait, traitLevel); }
  static canRetryWhiteCheck(checkId, trait) { return this.progression.canRetryWhiteCheck(checkId, trait); }
  static getFailedWhiteChecks() { return this.progression.getFailedWhiteChecks(); }
  static recordRedCheckResult(checkId, result) { return this.progression.recordRedCheckResult(checkId, result); }
  static getRedCheckResult(checkId) { return this.progression.getRedCheckResult(checkId); }
  static getRedCheckResults() { return this.progression.getRedCheckResults(); }
  static clearCheckHistory() { return this.progression.clearCheckHistory(); }

  // ==========================================
  // DIALOGUE REWARD CLAIMS (delegated to ProgressionState)
  // ==========================================

  static getRewardClaims() { return this.progression.getRewardClaims(); }
  static hasRewardClaim(claimKey) { return this.progression.hasRewardClaim(claimKey); }
  static recordRewardClaim(claimKey) { return this.progression.recordRewardClaim(claimKey); }
  static clearRewardClaims() { return this.progression.clearRewardClaims(); }

  // ==========================================
  // TUTORIAL / NOTIFICATIONS
  // ==========================================

  static hasShownNotification(notificationId) {
    if (!this.state.tutorial) {
      this.state.tutorial = { shownNotifications: [] };
    }
    return this.state.tutorial.shownNotifications.includes(notificationId);
  }

  static markNotificationShown(notificationId) {
    if (!this.state.tutorial) {
      this.state.tutorial = { shownNotifications: [] };
    }
    if (!this.state.tutorial.shownNotifications.includes(notificationId)) {
      this.state.tutorial.shownNotifications.push(notificationId);
    }
  }

  static resetTutorialState() {
    this.state.tutorial = { shownNotifications: [] };
  }

  // ==========================================
  // RUN MANAGEMENT
  // ==========================================

  static isRunActive() {
    return this.state.run.isActive;
  }

  static endRun() {
    this.state.run.isActive = false;
    this.save();
  }

  static startNewRun() {
    const memories = this.state.memories;
    const encyclopedia = this.state.encyclopedia || [];
    const username = this.state.player.username;
    const isLoggedIn = this.state.player.isLoggedIn;
    const runNumber = this.state.run.runNumber + 1;
    const traits = this.state.traits;
    const relations = this.state.relations;
    const failedWhiteChecks = this.state.failedWhiteChecks || {};
    const redCheckResults = this.state.redCheckResults || {};

    this._state = createDefaultState();

    this._state.memories = memories;
    this._state.encyclopedia = encyclopedia;
    this._state.player.username = username;
    this._state.player.isLoggedIn = isLoggedIn;
    this._state.run.runNumber = runNumber;
    this._state.run.isActive = true;
    this._state.traits = traits;
    this._state.relations = relations;
    this._state.failedWhiteChecks = failedWhiteChecks;
    this._state.redCheckResults = redCheckResults;

    // Set default pending battle for first battle (player's starting tile)
    this._state.map.pendingBattlePosition = { q: 0, r: 0 };

    this.save();

    // Notify all state keys since state was reset
    this._notify(STATE_KEYS.RUN);
    this._notify(STATE_KEYS.SPIRIT);
    this._notify(STATE_KEYS.DECK);
    this._notify(STATE_KEYS.EQUIPMENT);
    this._notify(STATE_KEYS.BATTLE_RESOURCES);
    this._notify(STATE_KEYS.MAP_POSITION);
    this._notify(STATE_KEYS.BLUEPRINTS);
    this._notify(STATE_KEYS.CONSTRUCTIONS);

    return this._state;
  }

  // ==========================================
  // DEBUG
  // ==========================================

  static debugTraits() {
    console.table({ ...this.state.traits, ...this.state.relations });
  }

  static exportState() {
    return JSON.stringify(this.state, null, 2);
  }

  static importState(jsonString) {
    try {
      this._state = JSON.parse(jsonString);
      this.save();
      return true;
    } catch (error) {
      console.error('Failed to import state:', error);
      return false;
    }
  }
}
